import { useNavigate } from 'react-router-dom'
import { createColumnHelper } from '@tanstack/react-table'

import styles from './CampaignsTable.module.scss'

import { CampaignListItem } from '@/api/generated'
import Button from '@/components/Button'
import DataTable from '@/components/DataTable'

interface CampaignsTableProps {
  campaigns: CampaignListItem[]
  isLoading: boolean
  onEdit: (campaign: CampaignListItem) => void
  onCreate: () => void
}

const columnHelper = createColumnHelper<CampaignListItem>()

const getStatusBadgeClass = (campaignStatus: string) => {
  switch (campaignStatus) {
    case 'active':
      return styles.badgeActive
    case 'paused':
      return styles.badgePaused
    case 'ended':
      return styles.badgeEnded
    case 'archived':
      return styles.badgeArchived
    default:
      return styles.badgeDefault
  }
}

const CampaignsTable = ({ campaigns, isLoading, onEdit, onCreate }: CampaignsTableProps) => {
  const navigate = useNavigate()

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => <span className={styles.campaignName}>{info.getValue()}</span>,
      sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor('campaign_type', {
      header: 'Type',
      cell: (info) => <span className={`${styles.badge} ${styles.badgeDefault}`}>{info.getValue()}</span>,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const campaignStatus = info.getValue()
        return <span className={`${styles.badge} ${getStatusBadgeClass(campaignStatus)}`}>{campaignStatus}</span>
      },
    }),
    columnHelper.accessor('trial_days', {
      header: 'Trial Days',
      sortingFn: 'basic',
    }),
    columnHelper.accessor('source_name', {
      header: 'Source',
    }),
    columnHelper.display({
      id: 'redemptions',
      header: 'Redemptions',
      cell: (info) => {
        const { total_redemptions, max_redemptions } = info.row.original
        return max_redemptions ? `${total_redemptions}/${max_redemptions}` : String(total_redemptions)
      },
    }),
    columnHelper.accessor('code_count', {
      header: 'Codes',
      sortingFn: 'basic',
    }),
    columnHelper.accessor('created_at', {
      header: 'Created',
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      sortingFn: 'datetime',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className={styles.actionButtons} onClick={(e) => e.stopPropagation()}>
          <Button type="dark" onClick={() => onEdit(info.row.original)} title="Edit" />
        </div>
      ),
    }),
  ]

  return (
    <>
      <div className={styles.tableControls}>
        <div className={styles.tableTitleSection}>
          <span className={styles.tableTitle}>Promo Campaigns</span>
        </div>
        <div className={styles.filters}>
          <Button title="+ Create Campaign" onClick={onCreate} type="dark" />
        </div>
      </div>
      <DataTable
        data={campaigns}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No campaigns yet. Click '+ Create Campaign' to get started."
        loadingMessage="Loading campaigns..."
        ariaLabel="Promo campaigns"
        onRowClick={(campaign) => navigate(`/app/campaigns/${campaign.id}`)}
      />
    </>
  )
}

export default CampaignsTable
